import { ITEMS, type ItemId } from '@/data/items';
import { RECIPES, type Recipe } from '@/data/recipes';
import { BAITS } from '@/data/tackle';
import { baitOf } from '@/data/items';
import {
  blockedReason,
  craft,
  craftName,
  describeCraftResult,
  ingredientStatus,
} from '@/world/crafting';
import { type Inventory } from '@/world/inventory';
import { BAG_SIZE } from '@/world/quickBag';
import { BagGrid } from '@/ui/bagGrid';
import type { ToolBelt } from '@/world/toolbelt';
import type { QuickBag } from '@/world/quickBag';
import type { WeaponGrowth } from '@/world/weaponGrowth';
import type { WeaponAbilities } from '@/world/weaponAbilities';
import { ABILITIES, ABILITY_IDS, ABILITY_SLOTS } from '@/data/abilities';
import { MAX_WEAPON_LEVEL, WEAPONS, type WeaponId, experienceToNext, isWeaponId } from '@/data/weapons';
import { evolutionBlocked, evolve, synthesise, synthesisBlocked } from '@/world/synthesis';

/**
 * Gear: tools, bag, bait and crafting.
 *
 * One screen with tabs rather than four buttons along the top edge. On a phone
 * the top edge is scarce and every extra button is a mis-tap during play; and
 * these four things are one loop — you open it because something will not work,
 * and the fix is a different tool, a different lure, or making one.
 */

export type GearTab = 'inventory' | 'tools' | 'bag' | 'bait' | 'craft';

export interface GearPanelOptions {
  readonly container: HTMLElement;
  readonly inventory: Inventory;
  readonly quickBag: QuickBag;
  readonly weaponGrowth: WeaponGrowth;
  readonly weaponAbilities: WeaponAbilities;
  readonly belt: ToolBelt;
  /** Patterns taught by finished quests. */
  readonly unlocked: () => ReadonlySet<string>;
  readonly getBait: () => ItemId | null;
  readonly onChooseBait: (bait: ItemId | null) => void;
  readonly onChanged: () => void;
  readonly onMessage: (message: string) => void;
  /** Put a backpack slot's stack on the ground at Katie's feet. */
  readonly onDrop: (index: number) => boolean;
}

export class GearPanel {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly tabs = new Map<GearTab, HTMLButtonElement>();
  private readonly options: GearPanelOptions;
  private readonly bagGrid: BagGrid;
  private tab: GearTab = 'inventory';
  /** Which bag slot is being pointed at something, if any. */
  private bagSlotBeingSet: number | null = null;

  constructor(options: GearPanelOptions) {
    this.options = options;
    this.bagGrid = new BagGrid({
      inventory: options.inventory,
      quickBag: options.quickBag,
      onChanged: options.onChanged,
      onMessage: options.onMessage,
      onDrop: options.onDrop,
    });

    this.root = document.createElement('div');
    this.root.className = 'gear-root';
    this.root.appendChild(this.createOpenButton());

    this.panel = document.createElement('div');
    this.panel.className = 'gear-panel';
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', 'Gear');

    const bar = document.createElement('div');
    bar.className = 'gear-tabs';
    for (const [id, label] of [
      ['inventory', 'Inventory'],
      ['tools', 'Tools'],
      ['bag', 'Bag'],
      ['bait', 'Bait'],
      ['craft', 'Craft'],
    ] as [GearTab, string][]) {
      const button = document.createElement('button');
      button.className = 'gear-tab';
      button.type = 'button';
      button.textContent = label;
      button.addEventListener('click', () => {
        this.tab = id;
        this.refresh();
      });
      this.tabs.set(id, button);
      bar.appendChild(button);
    }

    this.body = document.createElement('div');
    this.body.className = 'gear-body';

    const done = document.createElement('button');
    done.className = 'gear-done';
    done.type = 'button';
    done.textContent = 'Done';
    done.addEventListener('click', () => {
      this.setOpen(false);
    });

    this.panel.append(bar, this.body, done);
    this.root.appendChild(this.panel);
    options.container.appendChild(this.root);
  }

  setOpen(open: boolean, tab?: GearTab): void {
    if (tab) this.tab = tab;
    this.panel.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    if (open) this.refresh();
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  destroy(): void {
    this.bagGrid.destroy();
    this.root.remove();
  }

  private createOpenButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'gear-open';
    button.type = 'button';
    button.textContent = '🎒';
    button.setAttribute('aria-label', 'Gear');
    button.addEventListener('click', () => {
      this.setOpen(true);
    });
    return button;
  }

  private refresh(): void {
    for (const [id, button] of this.tabs) button.classList.toggle('is-active', id === this.tab);
    this.body.replaceChildren();

    switch (this.tab) {
      case 'tools':
        this.renderTools();
        break;
      case 'inventory':
        this.renderInventory();
        break;
      case 'bag':
        this.renderBag();
        break;
      case 'bait':
        this.renderBait();
        break;
      case 'craft':
        this.renderCraft();
        break;
    }
  }

  private renderTools(): void {
    const { belt, onChanged } = this.options;
    const grid = document.createElement('div');
    grid.className = 'gear-grid';

    // Only the best of each family is listed, because that is all Katie
    // carries — a superseded tool is gone, not sitting there to be picked by
    // mistake.
    for (const tool of belt.all()) {
      const button = document.createElement('button');
      button.className = 'gear-card';
      button.type = 'button';
      button.classList.toggle('is-chosen', belt.activeId === tool.id);

      const name = document.createElement('span');
      name.className = 'gear-card-name';
      name.textContent = tool.name;

      const note = document.createElement('span');
      note.className = 'gear-note';
      // A weapon says how far along it is, because "it is getting better" has
      // to be visible somewhere or it may as well not be happening.
      if (isWeaponId(tool.id)) {
        const level = this.options.weaponGrowth.levelFor(tool.id);
        const toNext = experienceToNext(this.options.weaponGrowth.experienceFor(tool.id));
        note.textContent =
          toNext === null
            ? `Level ${level} — fully grown`
            : `Level ${level} — ${toNext} more damage dealt`;
      } else {
        note.textContent = tool.family === 'rod' ? `Rod tier ${tool.tier}` : 'Tool';
      }

      button.append(name, note);
      button.addEventListener('click', () => {
        belt.setActive(tool.id);
        onChanged();
        this.refresh();
      });
      grid.appendChild(button);
    }

    this.body.appendChild(grid);

    // The blade in hand, or the best one carried. Falling back matters: the
    // panel used to vanish whenever a hoe was equipped, which reads as the
    // feature being missing rather than as it being about a weapon.
    const active = belt.activeTool;
    const weapon = active && isWeaponId(active.id) ? active : belt.best('sword');
    if (weapon && isWeaponId(weapon.id)) this.body.appendChild(this.createAbsorbPanel(weapon.id));
  }

  /**
   * Feeding what you killed into what you killed it with.
   *
   * Shown for the weapon in hand rather than all of them, because absorbing is
   * a decision about *this* blade and a list of every option for every weapon
   * is a spreadsheet.
   */
  private createAbsorbPanel(weapon: WeaponId): HTMLElement {
    const { inventory, weaponAbilities, onChanged, onMessage } = this.options;

    const panel = document.createElement('div');
    panel.className = 'absorb-panel';

    const title = document.createElement('p');
    title.className = 'gear-hint';
    const known = weaponAbilities.of(weapon);
    title.textContent =
      `${ITEMS[weapon].name} — ${known.length} of ${ABILITY_SLOTS} slots filled` +
      (known.length > 0 ? `: ${known.map((id) => ABILITIES[id].name).join(', ')}` : '');
    panel.appendChild(title);

    const list = document.createElement('div');
    list.className = 'gear-grid';

    for (const id of ABILITY_IDS) {
      const ability = ABILITIES[id];
      const blocked = weaponAbilities.blockedReason(weapon, id, inventory);

      const button = document.createElement('button');
      button.className = 'gear-card';
      button.type = 'button';
      button.disabled = blocked !== null;
      button.classList.toggle('is-chosen', weaponAbilities.has(weapon, id));

      const name = document.createElement('span');
      name.className = 'gear-card-name';
      name.textContent = ability.name;

      const note = document.createElement('span');
      note.className = 'gear-note';
      // Says why, rather than simply being grey.
      note.textContent =
        blocked === 'already-known'
          ? 'Already absorbed'
          : blocked === 'no-slots'
            ? 'No slots left on this weapon'
            : blocked === 'missing-materials'
              ? `Needs ${ability.cost} ${ITEMS[ability.material].name} — you have ${inventory.countOf(ability.material)}`
              : `${ability.note} Costs ${ability.cost} ${ITEMS[ability.material].name}.`;

      button.append(name, note);
      button.addEventListener('click', () => {
        if (weaponAbilities.absorb(weapon, id, inventory) === 'absorbed') {
          onMessage(`${ITEMS[weapon].name} absorbs ${ability.name}`);
          onChanged();
          this.refresh();
        }
      });
      list.appendChild(button);
    }

    panel.appendChild(list);

    const forgeState = {
      belt: this.options.belt,
      growth: this.options.weaponGrowth,
      abilities: weaponAbilities,
    };

    // Evolution first: it is the rarer, larger thing, and burying it under a
    // list of merge options would make it easy to miss entirely.
    const next = WEAPONS[weapon].evolvesTo;
    if (next) {
      const blocked = evolutionBlocked(weapon, forgeState);
      const button = document.createElement('button');
      button.className = 'gear-card';
      button.type = 'button';
      button.disabled = blocked !== null;

      const name = document.createElement('span');
      name.className = 'gear-card-name';
      name.textContent = `Evolve into ${ITEMS[next].name}`;

      const note = document.createElement('span');
      note.className = 'gear-note';
      note.textContent =
        blocked === 'not-ready'
          ? `Needs level ${MAX_WEAPON_LEVEL} and both slots filled`
          : `Keeps its abilities, gives up its levels. Consumes the ${ITEMS[weapon].name}.`;

      button.append(name, note);
      button.addEventListener('click', () => {
        if (evolve(weapon, forgeState) === 'evolved') {
          onMessage(`${ITEMS[weapon].name} becomes ${ITEMS[next].name}`);
          onChanged();
          this.refresh();
        }
      });
      panel.appendChild(button);
    }

    for (const other of this.options.belt.all()) {
      // Bound to a local so the narrowing survives into the click handler,
      // which runs long after this loop has finished.
      const sacrifice = other.id;
      if (!isWeaponId(sacrifice) || sacrifice === weapon) continue;

      const blocked = synthesisBlocked(weapon, sacrifice, forgeState);
      const button = document.createElement('button');
      button.className = 'gear-card';
      button.type = 'button';
      button.disabled = blocked !== null;

      const name = document.createElement('span');
      name.className = 'gear-card-name';
      name.textContent = `Merge in ${ITEMS[sacrifice].name}`;

      const note = document.createElement('span');
      note.className = 'gear-note';
      // Says plainly that a weapon is destroyed. This is the one button here
      // that cannot be undone.
      note.textContent =
        blocked === 'nothing-to-give'
          ? `${ITEMS[sacrifice].name} has nothing to pass on yet`
          : `Takes half its practice and any abilities that fit. Destroys the ${ITEMS[sacrifice].name}.`;

      button.append(name, note);
      button.addEventListener('click', () => {
        if (synthesise(weapon, sacrifice, forgeState) === 'synthesised') {
          onMessage(`${ITEMS[sacrifice].name} merged into ${ITEMS[weapon].name}`);
          onChanged();
          this.refresh();
        }
      });
      panel.appendChild(button);
    }

    return panel;
  }

  /** Everything the backpack is carrying. The screen opens here. */
  private renderInventory(): void {
    this.bagGrid.mount(this.body);
  }

  /**
   * The eight quick-access slots, editable in one place.
   *
   * The bar can be edited by tapping its own slots during play, but that is a
   * thing you do mid-game with one thumb; setting up a loadout deliberately
   * wants the whole row in front of you.
   */
  private renderBag(): void {
    const { inventory, quickBag, onChanged } = this.options;

    const hint = document.createElement('p');
    hint.className = 'gear-hint';
    hint.textContent = 'What your thumb can reach without opening this screen.';
    this.body.appendChild(hint);

    const grid = document.createElement('div');
    grid.className = 'gear-slots';

    for (let index = 0; index < BAG_SIZE; index++) {
      const id = quickBag.assignmentAt(index);
      const button = document.createElement('button');
      button.className = 'gear-slot';
      button.type = 'button';
      button.classList.toggle('is-empty', id === null);
      button.classList.toggle('is-selected', index === quickBag.selectedIndex);

      if (id) {
        const definition = ITEMS[id];
        const held = inventory.countOf(id);
        button.style.setProperty('--tint', definition.tint);
        button.classList.toggle('is-out', held === 0);

        const label = document.createElement('span');
        label.className = 'gear-slot-label';
        label.textContent = definition.label;

        const count = document.createElement('span');
        count.className = 'gear-slot-count';
        count.textContent = definition.stackSize > 1 ? String(held) : '';

        button.append(label, count);
        button.setAttribute('aria-label', `Bag slot ${index + 1}, ${definition.name}, ${held}`);
      } else {
        button.setAttribute('aria-label', `Bag slot ${index + 1}, empty`);
      }

      button.addEventListener('click', () => {
        this.bagSlotBeingSet = this.bagSlotBeingSet === index ? null : index;
        this.refresh();
      });
      grid.appendChild(button);
    }

    this.body.appendChild(grid);

    if (this.bagSlotBeingSet !== null) {
      this.body.appendChild(this.createBagChooser(this.bagSlotBeingSet));
    }

    void onChanged;
  }

  /** Pick what a bag slot points at, from what the backpack is carrying. */
  private createBagChooser(index: number): HTMLElement {
    const { inventory, quickBag, onChanged } = this.options;

    const panel = document.createElement('div');
    panel.className = 'bag-assign-panel';

    const title = document.createElement('p');
    title.className = 'gear-hint';
    title.textContent = `Bag slot ${index + 1}`;
    panel.appendChild(title);

    const list = document.createElement('div');
    list.className = 'bag-picker-list';

    const seen = new Set<ItemId>();
    for (let i = 0; i < inventory.capacity; i++) {
      const slot = inventory.slotAt(i);
      if (!slot || seen.has(slot.id)) continue;
      seen.add(slot.id);

      const definition = ITEMS[slot.id];
      const option = document.createElement('button');
      option.className = 'bag-picker-option';
      option.type = 'button';
      option.style.setProperty('--tint', definition.tint);
      option.textContent = `${definition.name}${
        definition.stackSize > 1 ? ` x${inventory.countOf(slot.id)}` : ''
      }`;
      option.addEventListener('click', () => {
        quickBag.assign(index, slot.id);
        this.bagSlotBeingSet = null;
        onChanged();
        this.refresh();
      });
      list.appendChild(option);
    }

    if (seen.size === 0) {
      const empty = document.createElement('p');
      empty.className = 'gear-hint';
      empty.textContent = 'Your backpack is empty.';
      panel.appendChild(empty);
    }

    panel.appendChild(list);

    if (quickBag.assignmentAt(index) !== null) {
      const clear = document.createElement('button');
      clear.className = 'bag-picker-cancel';
      clear.type = 'button';
      clear.textContent = 'Clear this slot';
      clear.addEventListener('click', () => {
        quickBag.assign(index, null);
        this.bagSlotBeingSet = null;
        onChanged();
        this.refresh();
      });
      panel.appendChild(clear);
    }

    return panel;
  }

  private renderBait(): void {
    const { inventory, getBait, onChooseBait } = this.options;
    const owned = Object.values(BAITS)
      .map((bait) => `bait:${bait.id}` as ItemId)
      .filter((id) => inventory.countOf(id) > 0);

    if (owned.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'gear-hint';
      empty.textContent = 'No bait. Hoe some grass to turn up worms.';
      this.body.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'gear-grid';

    for (const id of owned) {
      const bait = BAITS[baitOf(id)!];
      const button = document.createElement('button');
      button.className = 'gear-card';
      button.type = 'button';
      button.classList.toggle('is-chosen', getBait() === id);
      button.style.setProperty('--tint', bait.tint);

      const name = document.createElement('span');
      name.className = 'gear-card-name';
      name.textContent = bait.name;

      const count = document.createElement('span');
      count.className = 'gear-card-count';
      count.textContent = bait.reusable ? 'lure' : `x${inventory.countOf(id)}`;

      const note = document.createElement('span');
      note.className = 'gear-note';
      note.textContent = bait.description;

      button.append(name, count, note);
      button.addEventListener('click', () => {
        onChooseBait(getBait() === id ? null : id);
        this.refresh();
      });
      grid.appendChild(button);
    }

    this.body.appendChild(grid);
  }

  private renderCraft(): void {
    const grid = document.createElement('div');
    grid.className = 'gear-grid';
    // A pattern you have not been taught is absent rather than greyed out.
    // The reward for the quest is being handed it; a slot in the list saying
    // "not yet" hands it to you weeks early.
    const unlocked = this.options.unlocked();
    for (const recipe of RECIPES) {
      if (recipe.locked && !unlocked.has(recipe.id)) continue;
      grid.appendChild(this.renderRecipe(recipe));
    }
    this.body.appendChild(grid);
  }

  private renderRecipe(recipe: Recipe): HTMLButtonElement {
    const { inventory, belt, onMessage, onChanged } = this.options;
    const context = { inventory, belt, unlocked: this.options.unlocked() };
    const blocked = blockedReason(recipe, context);

    const button = document.createElement('button');
    button.className = 'gear-card';
    button.type = 'button';
    button.classList.toggle('is-ready', blocked === null);
    button.classList.toggle('is-dim', blocked === 'already-owned' || blocked === 'not-yet');

    const name = document.createElement('span');
    name.className = 'gear-card-name';
    name.textContent = craftName(recipe);

    const status = document.createElement('span');
    status.className = 'gear-card-count';
    status.textContent =
      blocked === null
        ? 'ready'
        : blocked === 'already-owned'
          ? 'owned'
          : blocked === 'not-yet'
            ? 'locked'
            : '';

    const note = document.createElement('span');
    note.className = 'gear-note';
    note.textContent = recipe.note;

    // have/need rather than "not enough": it tells the player what to go and do.
    const parts = document.createElement('span');
    parts.className = 'gear-parts';
    parts.textContent = ingredientStatus(recipe, inventory)
      .map((part) => `${ITEMS[part.item as ItemId].name} ${part.have}/${part.need}`)
      .join('  ·  ');

    button.append(name, status, note, parts);
    button.addEventListener('click', () => {
      onMessage(describeCraftResult(craft(recipe, context), craftName(recipe)));
      onChanged();
      this.refresh();
    });

    return button;
  }
}
